<template>
  <div class="page-content">
    <!-- Page header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Tableau de Bord</h1>
        <p class="page-subtitle">{{ todayFormatted }}</p>
      </div>
      <v-btn outlined small color="primary" @click="loadDashboard" :loading="loading">
        <v-icon left small>mdi-refresh</v-icon>
        Actualiser
      </v-btn>
    </div>

    <!-- Loading state -->
    <div v-if="loading && !hasData" class="page-loader">
      <v-progress-circular indeterminate color="primary" size="40" width="3"></v-progress-circular>
    </div>

    <div v-if="hasData">
      <!-- 1. SUMMARY CARDS -->
      <v-row class="mb-2">
        <v-col cols="6" md="3" v-for="card in summaryCards" :key="card.label">
          <v-card flat class="summary-card">
            <div class="summary-card-inner">
              <div class="summary-icon" :style="{ background: card.bgColor }">
                <v-icon color="white" size="22">{{ card.icon }}</v-icon>
              </div>
              <div class="summary-info">
                <div class="summary-value">{{ card.value }}</div>
                <div class="summary-label">{{ card.label }}</div>
                <div class="summary-compare" v-if="card.compare !== null">
                  <v-icon x-small :color="card.compareUp ? 'success' : 'error'">
                    {{ card.compareUp ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                  </v-icon>
                  <span :class="card.compareUp ? 'success--text' : 'error--text'">
                    {{ card.compare }}
                  </span>
                  <span class="text--secondary"> {{ card.compareLabel }}</span>
                </div>
                <div class="summary-compare summary-compare--placeholder" v-else>&nbsp;</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- 2. SALES BY HOUR CHART -->
      <v-row>
        <v-col cols="12">
          <v-card flat class="dash-card">
            <v-card-title class="dash-card-title">
              <v-icon color="primary" size="18" class="mr-2">mdi-chart-bar</v-icon>
              {{ chartTitle }}
            </v-card-title>
            <v-card-text>
              <div class="chart-wrapper" v-if="salesByHour.labels.length > 0">
                <bar-chart ref="hourChart" :chart-data="hourlyChartData" :options="hourlyChartOptions" />
              </div>
              <div v-else class="empty-state">
                <v-icon size="40" color="grey lighten-1">mdi-chart-bar</v-icon>
                <p>Aucune vente aujourd'hui</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- 3 & 4. TOP PRODUCTS + LOW STOCK -->
      <v-row>
        <v-col cols="12" md="7">
          <v-card flat class="dash-card">
            <v-card-title class="dash-card-title">
              <v-icon color="secondary" size="18" class="mr-2">mdi-trophy-outline</v-icon>
              Produits les plus vendus
            </v-card-title>
            <v-card-text class="pa-0">
              <v-simple-table v-if="topProducts.length > 0" dense>
                <thead>
                  <tr>
                    <th class="text-left">#</th>
                    <th class="text-left">Produit</th>
                    <th class="text-right">Qté</th>
                    <th class="text-right">Revenu</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in topProducts" :key="i">
                    <td class="text-caption font-weight-bold" style="width: 32px;">{{ i + 1 }}</td>
                    <td>{{ p.name }}</td>
                    <td class="text-right">
                      <v-chip x-small color="primary" dark>{{ p.qty_sold }}</v-chip>
                    </td>
                    <td class="text-right font-weight-medium">{{ formatCurrency(p.revenue) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else class="empty-state">
                <v-icon size="40" color="grey lighten-1">mdi-package-variant</v-icon>
                <p>Aucun produit vendu aujourd'hui</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="5">
          <v-card flat class="dash-card">
            <v-card-title class="dash-card-title">
              <v-icon color="error" size="18" class="mr-2">mdi-alert-outline</v-icon>
              Alertes de stock
              <v-chip
                v-if="lowStockCount > 0"
                x-small
                color="error"
                dark
                class="ml-2"
              >
                {{ lowStockCount }}
              </v-chip>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-list dense v-if="lowStock.length > 0" class="pa-0">
                <v-list-item v-for="(item, i) in lowStock" :key="i" class="stock-item">
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ item.stock > 0 ? ('Seuil : ' + item.stock_alert) : 'Rupture de stock' }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip
                      x-small
                      :color="item.stock <= 0 ? 'error' : 'warning'"
                      dark
                    >
                      {{ item.stock }} restant{{ item.stock > 1 ? 's' : '' }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <div v-else class="empty-state success--text">
                <v-icon size="40" color="success">mdi-check-circle-outline</v-icon>
                <p>Stock OK</p>
              </div>
            </v-card-text>
            <v-card-actions v-if="lowStockCount > 0" class="pt-0">
              <v-btn
                text
                small
                color="error"
                block
                @click="openStockAlerts"
              >
                <v-icon size="18" class="mr-1">mdi-format-list-bulleted</v-icon>
                Voir tout ({{ lowStockCount }})
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- 5. RECENT TRANSACTIONS -->
      <v-row>
        <v-col cols="12">
          <v-card flat class="dash-card">
            <v-card-title class="dash-card-title">
              <v-icon color="info" size="18" class="mr-2">mdi-receipt-text-outline</v-icon>
              Transactions récentes
            </v-card-title>
            <v-card-text class="pa-0">
              <v-simple-table v-if="recentTransactions.length > 0" dense>
                <thead>
                  <tr>
                    <th class="text-left">Date</th>
                    <th class="text-left">Heure</th>
                    <th class="text-left">Réf.</th>
                    <th class="text-left">Agent</th>
                    <th class="text-center">Articles</th>
                    <th class="text-right">Montant</th>
                    <th class="text-center">Statut</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tx in recentTransactions.slice(0, 10)" :key="tx.id">
                    <td class="text-caption">{{ formatDate(tx.created_at) }}</td>
                    <td class="text-caption">{{ formatTime(tx.created_at) }}</td>
                    <td class="font-weight-medium">{{ tx.sale_number }}</td>
                    <td class="text-caption">{{ tx.agent }}</td>
                    <td class="text-center">{{ tx.items }}</td>
                    <td class="text-right font-weight-medium">{{ formatCurrency(tx.total_htg) }}</td>
                    <td class="text-center">
                      <v-chip x-small :color="tx.status ? 'success' : 'error'" dark>
                        {{ tx.status ? 'OK' : 'Annulé' }}
                      </v-chip>
                    </td>
                    <td style="width:40px">
                      <v-btn icon x-small @click="$router.push('/sales/' + tx.id)">
                        <v-icon small color="primary">mdi-eye-outline</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else class="empty-state">
                <v-icon size="40" color="grey lighten-1">mdi-receipt-text-outline</v-icon>
                <p>Aucune transaction aujourd'hui</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- 6. LAST 7 DAYS -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card flat class="dash-card">
            <v-card-title class="dash-card-title">
              <v-icon color="primary" size="18" class="mr-2">mdi-chart-line</v-icon>
              7 derniers jours
            </v-card-title>
            <v-card-text>
              <div class="chart-wrapper chart-wrapper--small" v-if="last7Days.labels.length > 0">
                <line-chart ref="weekChart" :chart-data="weekChartData" :options="weekChartOptions" />
              </div>
              <div v-else class="empty-state">
                <v-icon size="40" color="grey lighten-1">mdi-chart-line</v-icon>
                <p>Pas de données</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Error state -->
    <v-alert v-if="error" type="error" text dense class="mt-4">
      {{ error }}
    </v-alert>

    <!-- Stock alerts modal: liste complète + export Excel -->
    <v-dialog v-model="stockAlertsDialog" max-width="720" scrollable>
      <v-card>
        <v-card-title class="dash-card-title">
          <v-icon color="error" size="20" class="mr-2">mdi-alert-outline</v-icon>
          Produits en alerte de stock
          <v-spacer></v-spacer>
          <v-btn icon @click="stockAlertsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text style="max-height: 60vh" class="pa-0">
          <div v-if="stockAlertsLoading" class="pa-8 text-center">
            <v-progress-circular indeterminate color="error"></v-progress-circular>
            <p class="mt-3 grey--text">Chargement…</p>
          </div>

          <v-alert v-else-if="stockAlertsError" type="error" text dense class="ma-4">
            {{ stockAlertsError }}
          </v-alert>

          <v-simple-table v-else dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Produit</th>
                  <th class="text-right">Stock</th>
                  <th class="text-right">Seuil</th>
                  <th class="text-center">État</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in stockAlertsList" :key="i">
                  <td>{{ item.name }}</td>
                  <td class="text-right">{{ item.stock }}</td>
                  <td class="text-right">{{ item.stock_alert > 0 ? item.stock_alert : '—' }}</td>
                  <td class="text-center">
                    <v-chip x-small :color="item.stock <= 0 ? 'error' : 'warning'" dark>
                      {{ item.stock > 0 ? 'Faible' : 'Rupture' }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <span class="grey--text text-body-2">
            {{ stockAlertsList.length }} produit{{ stockAlertsList.length > 1 ? 's' : '' }}
          </span>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            :disabled="stockAlertsLoading || stockAlertsList.length === 0"
            @click="exportStockAlerts"
          >
            <v-icon size="18" class="mr-1">mdi-microsoft-excel</v-icon>
            Exporter Excel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Bar, Line } from 'vue-chartjs'
import moment from 'moment'
import numeral from 'numeral'
import { exportStockAlertsExcel } from '@/utils/stockAlertsExport'

const chartDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  layout: {
    padding: { top: 8, right: 12, bottom: 4, left: 4 }
  },
  tooltips: {
    backgroundColor: 'rgba(26, 35, 50, 0.9)',
    titleFontFamily: 'Inter, sans-serif',
    bodyFontFamily: 'Inter, sans-serif',
    cornerRadius: 6,
    xPadding: 10,
    yPadding: 8,
  },
  scales: {
    yAxes: [{
      ticks: { beginAtZero: true, fontColor: '#64748b', fontFamily: 'Inter, sans-serif', fontSize: 11 },
      gridLines: { color: 'rgba(0,0,0,0.04)', drawBorder: false }
    }],
    xAxes: [{
      ticks: { fontColor: '#475569', fontFamily: 'Inter, sans-serif', fontSize: 11, padding: 6, maxRotation: 45, minRotation: 0 },
      gridLines: { display: false }
    }]
  },
  plugins: { datalabels: false }
}

export default {
  name: 'HomeView',

  data: () => ({
    loading: false,
    error: null,
    hasData: false,
    isToday: true,
    dateFrom: null,
    dateTo: null,
    summary: null,
    salesByHour: { labels: [], data: [] },
    topProducts: [],
    lowStock: [],
    lowStockCount: 0,
    stockAlertsDialog: false,
    stockAlertsLoading: false,
    stockAlertsError: null,
    stockAlertsList: [],
    recentTransactions: [],
    last7Days: { labels: [], data: [] },
  }),

  computed: {
    todayFormatted() {
      if (!this.dateFrom || !this.dateTo) return moment().format('dddd D MMMM YYYY')
      if (this.dateFrom === this.dateTo) {
        return moment(this.dateFrom).format('dddd D MMMM YYYY')
      }
      return moment(this.dateFrom).format('D MMM YYYY') + ' — ' + moment(this.dateTo).format('D MMM YYYY')
    },

    chartTitle() {
      if (this.isToday) return 'Ventes aujourd\'hui par heure'
      if (this.dateFrom === this.dateTo) return 'Ventes par heure — ' + moment(this.dateFrom).format('D MMM YYYY')
      return 'Ventes par jour'
    },

    summaryCards() {
      if (!this.summary) return []
      const s = this.summary
      const prevTotal = s.prev_total || 0
      const prevTx = s.prev_transactions || 0
      const prevAvg = prevTx > 0 ? prevTotal / prevTx : 0

      const pctChange = (current, prev) => {
        if (prev === 0) return current > 0 ? '+100%' : null
        const pct = Math.round(((current - prev) / prev) * 100)
        return (pct >= 0 ? '+' : '') + pct + '%'
      }

      const compareLabel = this.isToday ? 'vs hier' : 'vs période préc.'

      return [
        {
          label: this.isToday ? 'Ventes Aujourd\'hui' : 'Total Ventes',
          value: this.formatCurrency(s.total_sales),
          icon: 'mdi-cash-register',
          bgColor: '#194173',
          compare: pctChange(s.total_sales, prevTotal),
          compareUp: s.total_sales >= prevTotal,
          compareLabel: compareLabel,
        },
        {
          label: 'Transactions',
          value: s.transactions.toLocaleString(),
          icon: 'mdi-receipt-text-outline',
          bgColor: '#f68d3b',
          compare: pctChange(s.transactions, prevTx),
          compareUp: s.transactions >= prevTx,
          compareLabel: compareLabel,
        },
        {
          label: 'Vente Moyenne',
          value: this.formatCurrency(s.average_sale),
          icon: 'mdi-chart-timeline-variant',
          bgColor: '#4CAF50',
          compare: pctChange(s.average_sale, prevAvg),
          compareUp: s.average_sale >= prevAvg,
          compareLabel: compareLabel,
        },
        {
          label: 'Articles Vendus',
          value: s.items_sold.toLocaleString(),
          icon: 'mdi-package-variant-closed',
          bgColor: '#2196F3',
          compare: pctChange(s.items_sold, s.prev_items_sold || 0),
          compareUp: s.items_sold >= (s.prev_items_sold || 0),
          compareLabel: compareLabel,
        },
      ]
    },

    hourlyChartData() {
      return {
        labels: this.salesByHour.labels,
        datasets: [{
          label: 'Ventes (HTG)',
          backgroundColor: 'rgba(25, 65, 115, 0.75)',
          borderRadius: 4,
          borderSkipped: false,
          data: this.salesByHour.data,
          barThickness: 24,
        }]
      }
    },
    hourlyChartOptions() { return { ...chartDefaults } },

    weekChartData() {
      return {
        labels: this.last7Days.labels,
        datasets: [{
          label: 'Ventes (HTG)',
          backgroundColor: 'rgba(25, 65, 115, 0.06)',
          borderColor: '#194173',
          borderWidth: 2,
          pointBackgroundColor: '#194173',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
          data: this.last7Days.data,
          fill: true,
          tension: 0.4,
        }]
      }
    },
    weekChartOptions() { return { ...chartDefaults } },
  },

  methods: {
    formatCurrency(val) {
      return numeral(val).format('0,0') + ' HTG'
    },

    async openStockAlerts() {
      this.stockAlertsDialog = true
      await this.fetchStockAlerts()
    },

    async fetchStockAlerts() {
      this.stockAlertsLoading = true
      this.stockAlertsError = null
      try {
        const business = JSON.parse(localStorage.business)
        const response = await this.$axios.get('/businesses/' + business.id + '/stock-alerts')
        if (response.data.success) {
          this.stockAlertsList = response.data.data
          this.lowStockCount = this.stockAlertsList.length
        } else {
          this.stockAlertsError = response.data.message || 'Erreur lors du chargement'
        }
      } catch (e) {
        console.error('Stock alerts error:', e)
        this.stockAlertsError = 'Impossible de charger la liste des alertes de stock'
      } finally {
        this.stockAlertsLoading = false
      }
    },

    exportStockAlerts() {
      if (this.stockAlertsList.length === 0) return
      let businessName = ''
      try {
        businessName = (JSON.parse(localStorage.business).name) || ''
      } catch (e) {
        businessName = ''
      }
      const stamp = moment().format('YYYY-MM-DD')
      exportStockAlertsExcel(this.stockAlertsList, {
        businessName,
        filename: 'alertes_de_stock_' + stamp + '.xlsx',
      })
    },

    formatDate(dateStr) {
      return moment(dateStr).format('DD/MM/YY')
    },

    formatTime(dateStr) {
      return moment(dateStr).format('HH:mm')
    },

    getFilterDates() {
      const isValid = (val) => val && val !== 'null' && val !== 'undefined' && val.trim() !== ''
      const from = localStorage.getItem('debut_periode')
      const to = localStorage.getItem('fin_periode')
      return {
        from: isValid(from) ? from : moment().format('YYYY-MM-DD'),
        to: isValid(to) ? to : moment().format('YYYY-MM-DD'),
      }
    },

    async loadDashboard() {
      this.loading = true
      this.error = null

      try {
        const business = JSON.parse(localStorage.business)
        const dates = this.getFilterDates()
        const response = await this.$axios.get(
          '/businesses/pos-dashboard/' + business.id + '?from=' + dates.from + '&to=' + dates.to
        )

        if (response.data.success) {
          const d = response.data.data
          this.dateFrom = d.from
          this.dateTo = d.to
          this.isToday = d.is_today
          this.summary = d.summary
          this.salesByHour = d.sales_by_hour
          this.topProducts = d.top_products
          this.lowStock = d.low_stock
          this.lowStockCount = d.low_stock_count != null ? d.low_stock_count : d.low_stock.length
          this.recentTransactions = d.recent_transactions
          this.last7Days = d.last_7_days
          this.hasData = true

          // Re-render charts
          this.$nextTick(() => {
            if (this.$refs.hourChart && this.salesByHour.labels.length > 0) {
              this.$refs.hourChart.renderChart(this.hourlyChartData, this.hourlyChartOptions)
            }
            if (this.$refs.weekChart && this.last7Days.labels.length > 0) {
              this.$refs.weekChart.renderChart(this.weekChartData, this.weekChartOptions)
            }
          })
        } else {
          this.error = response.data.message || 'Erreur lors du chargement'
        }
      } catch (e) {
        console.error('Dashboard error:', e)
        this.error = 'Impossible de charger le tableau de bord. Vérifiez votre connexion.'
      } finally {
        this.loading = false
      }
    },
  },

  mounted() {
    moment.locale('fr')
    this.loadDashboard()
  },

  components: {
    BarChart: {
      extends: Bar,
      props: ['chartData', 'options'],
      mounted() { setTimeout(() => this.renderChart(this.chartData, this.options), 0) }
    },
    LineChart: {
      extends: Line,
      props: ['chartData', 'options'],
      mounted() { setTimeout(() => this.renderChart(this.chartData, this.options), 0) }
    },
  },

  watch: {
    hourlyChartData: {
      handler(d) { if (this.$refs.hourChart) this.$refs.hourChart.renderChart(d, this.hourlyChartOptions) },
      deep: true
    },
    weekChartData: {
      handler(d) { if (this.$refs.weekChart) this.$refs.weekChart.renderChart(d, this.weekChartOptions) },
      deep: true
    },
  },
}
</script>

<style scoped>
/* Summary cards */
.summary-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
  padding: 16px;
}

.summary-card-inner {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.summary-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-info {
  min-width: 0;
  flex: 1;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a2332;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 600px) {
  .summary-value {
    font-size: 1rem;
  }
}

.summary-label {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 2px;
}

.summary-compare {
  font-size: 0.6875rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 2px;
  min-height: 16px;
}

.summary-compare--placeholder {
  visibility: hidden;
}

/* Chart wrappers - canvas must be fully contained */
.chart-wrapper {
  position: relative;
  width: 100%;
  height: 320px;
  overflow: hidden;
  padding-bottom: 8px;
}

.chart-wrapper canvas {
  max-height: 100% !important;
}

.chart-wrapper--small {
  height: 280px;
}

/* Dashboard cards */
.dash-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
  margin-bottom: 8px;
  overflow: hidden;
}

.dash-card-title {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  padding: 14px 16px 8px !important;
  color: #1a2332;
}

/* Tables inside cards */
.dash-card .v-data-table,
.dash-card .v-simple-table {
  border: none !important;
  box-shadow: none !important;
}

.dash-card .v-simple-table thead th {
  font-size: 0.6875rem !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8 !important;
  background: transparent !important;
  border-bottom: 1px solid #e8ecf1 !important;
  padding: 8px 16px !important;
}

.dash-card .v-simple-table tbody td {
  font-size: 0.8125rem;
  padding: 10px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.dash-card .v-simple-table tbody tr:last-child td {
  border-bottom: none !important;
}

.dash-card .v-simple-table tbody tr:hover {
  background: rgba(25, 65, 115, 0.02) !important;
}

/* Stock alert items */
.stock-item {
  border-bottom: 1px solid #f1f5f9;
  min-height: 48px !important;
  padding: 4px 16px !important;
}

.stock-item:last-child {
  border-bottom: none;
}

/* Empty states */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  color: #94a3b8;
}

.empty-state p {
  margin: 8px 0 0;
  font-size: 0.8125rem;
}
</style>
